import dayjs from 'dayjs';
import axiosInstance from '../variables/api/axiosInstance';
import i18n from '../i18n';

/**
 * Fiche de déclaration du bureau homologue - sinistres TE.
 *
 * L'accident a eu lieu dans un autre pays : le bureau homologue instruit le dossier
 * et adresse lui-même la demande de confirmation de garantie à la compagnie
 * togolaise, au moyen de sa fiche de déclaration. Le dossier est enregistré à
 * partir de cette fiche, qui doit donc l'accompagner.
 *
 * Côté serveur, un TE pris en charge avec sa fiche passe directement en attente de
 * confirmation de la compagnie ; sans elle, il reste en NOUVEAU.
 */
export const TYPE_FICHE_HOMOLOGUE = 'FICHE_DECLARATION_HOMOLOGUE';

export const estSinistreTe = (sinistre) => sinistre?.typeSinistre === 'SURVENU_ETRANGER';

export const ficheHomologueParmi = (documents) =>
  Array.isArray(documents) && documents.some(d => d?.typeDocument === TYPE_FICHE_HOMOLOGUE);

/** Statuts où la fiche manquante bloque la suite : avant la réponse de la compagnie. */
const STATUTS_FICHE_ATTENDUE = ['SOUMISE', 'NOUVEAU', 'VALIDE', 'GARANTIE_ATTENTE_CONFIRMATION'];

/**
 * Faut-il réclamer la fiche sur ce dossier ? Pas sur un dossier repris : les TE
 * historiques n'ont jamais eu de fiche dans la GED, et n'en auront pas.
 */
export const ficheHomologueAttendue = (sinistre) =>
  estSinistreTe(sinistre)
  && !sinistre.repriseHistorique
  && STATUTS_FICHE_ATTENDUE.includes(sinistre.statut);

/** Téléverse un PDF dans la GED du dossier. */
export async function joindreDocumentGed(sinistreTrackingId, file, { titre, typeDocument, dateDocument }) {
  const metadata = {
    titre,
    typeDocument,
    dateDocument: dateDocument ? dayjs(dateDocument).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
    sinistreTrackingId,
  };
  const formData = new FormData();
  formData.append('file', file);
  formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  return axiosInstance.post('/v1/ged/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/**
 * Joint la fiche puis les autres pièces reçues de l'homologue. Ne s'arrête pas au
 * premier échec : le dossier existe déjà, chaque pièce enregistrée compte.
 *
 * @return la liste des pièces qui n'ont pas pu être jointes (libellés)
 */
export async function joindrePiecesHomologue(sinistre, { fiche, autres = [] }, dateReception) {
  const numero = sinistre?.numeroSinistreLocal || '';
  const echecs = [];
  if (fiche) {
    try {
      await joindreDocumentGed(sinistre.sinistreTrackingId, fiche, {
        // Titre enregistré dans la GED, lu par tous : il reste en français.
        titre: `Fiche de déclaration homologue - ${numero}`,
        typeDocument: TYPE_FICHE_HOMOLOGUE,
        dateDocument: dateReception,
      });
    } catch {
      echecs.push(i18n.t('referentiels:ficheHomologue.libelleEchec'));
    }
  }
  for (const piece of autres) {
    try {
      await joindreDocumentGed(sinistre.sinistreTrackingId, piece, {
        titre: `${piece.name.replace(/\.pdf$/i, '')} - ${numero}`,
        typeDocument: 'AUTRES',
        dateDocument: dateReception,
      });
    } catch {
      echecs.push(piece.name);
    }
  }
  return echecs;
}
